import { Gamepad2, Zap, ShieldCheck, Smartphone } from "lucide-react";
import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async"; 
import { FaRocket } from "react-icons/fa";

const WhyChooseUs = () => {

useEffect(() => {
  const loadAOS = async () => {
    const AOS = await import('aos');
    AOS.init({
      duration:500,
      easing: "ease-out-cubic",
      once: true,
      offset: 100
    });
  };
  loadAOS();
}, []);

// 🚀 NATURAL HUMAN WRITTEN CONTENT FOR HIGH VALUE
const features = [
  {
    title: "Play instantly, no downloads",
    icon: <Zap className="w-10 h-10 text-yellow-400" aria-hidden="true" />,
    desc: "Nobody likes waiting for a game to download. At Game Zone Hub, you don't have to. You can play directly in your web browser, which means you save storage space on your phone or PC. Just click on a game you like and start playing right away without installing anything.",
  },
  {
    title: "Safe and secure",
    icon: <ShieldCheck className="w-10 h-10 text-green-400" aria-hidden="true" />,
    desc: "We want you to have fun without worrying about viruses or hidden fees. Our website is completely free to use, and because the games run in your browser, you don't have to download any risky files. It is a safe place for everyone in the family to play.",
  },
  {
    title: "Works on all your devices",
    icon: <Smartphone className="w-10 h-10 text-blue-400" aria-hidden="true" />,
    desc: "Start a puzzle on your laptop and finish it on your phone while on the bus. Our games are built to work perfectly no matter what device you use. The screen adjusts automatically, so you can use your keyboard and mouse or just swipe and tap on your mobile screen.",
  },
  {
    title: "Huge variety of games",
    icon: <Gamepad2 className="w-10 h-10 text-pink-400" aria-hidden="true" />,
    desc: "We are always adding new games so you never get bored. Whether you are into fast car racing, tricky brain puzzles, or fun multiplayer games with friends, you will find it here. We handpick the best games to make sure you always have something fun to do.",
  },
];

 const schemaData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": features.map((feature, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": feature.title,
      "description": feature.desc
    }))
};

  return (
    <section className="bg-gradient-to-b lg:px-8 from-[#0f172a] to-[#1e293b] sm:py-20 py-12">
       <Helmet>
        {schemaData && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
        )}
      </Helmet>
      <div className="container mx-auto px-4 ">
        
        {/* 🚀 NATURAL HUMAN WRITTEN HEADER TEXT */}
        <div className="text-center mb-16">
            <h2
            className="text-center text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-extrabold text-white mb-6 flex flex-wrap justify-center items-center gap-4"
            data-aos="fade-up"
            data-aos-delay="100"
            >
            <FaRocket className="text-cyan-400 animate-bounce sm:animate-none sm:hover:-translate-y-1 transition-transform duration-300" />
            <div>
                <span className="whitespace-nowrap">Why Play on{" "}</span>
                <span className="bg-clip-text whitespace-nowrap text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-300">
                Game Zone Hub?
                </span>
            </div>
            </h2>
            <div data-aos="fade-up" data-aos-delay="200" className="space-y-4 max-w-4xl mx-auto text-gray-400 sm:text-lg text-[16px] leading-relaxed">
                <p>
                    Gaming shouldn't be complicated or expensive. That is why we created Game Zone Hub. We believe you should be able to jump into a great game for free, whenever you want, without needing an expensive console or waiting for massive updates. 
                </p>
                <p>
                    Our focus is just giving you a fast, smooth, and fun experience. Whether you want to kill five minutes on your coffee break or spend a few hours trying to beat a high score, everything you need is right here in your browser.
                </p>
            </div>
        </div>

        {/* FEATURES GRID */}
        <ul
          className="grid lg:grid-cols-4 md:grid-cols-2 gap-8"
          data-aos="fade-up"
          data-aos-delay="300"
        >
          {features.map((feature, index) => (
            <li
              key={index}
              data-aos="zoom-in"
              data-aos-delay={`${300 + index * 100}`}
              className="relative bg-[#1e293b] rounded-3xl cursor-pointer p-8 text-center
                         border border-white/5 hover:border-cyan-400/50
                         transition-all duration-500 group
                         shadow-[0_10px_30px_rgba(0,0,0,0.5)] 
                         hover:shadow-[0_0_40px_rgba(0,200,255,0.3)]
                         hover:scale-105 hover:-translate-y-2 overflow-hidden flex flex-col h-full"
            >
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none">
                <div className="absolute top-0 left-0 w-40 h-40 bg-cyan-400/10 blur-3xl rounded-full"></div>
                <div className="absolute bottom-0 right-0 w-40 h-40 bg-purple-500/10 blur-3xl rounded-full"></div>
              </div>

              <div
                className="flex justify-center mb-6 relative z-10 group-hover:scale-110 transition-transform duration-300 bg-black/20 w-20 h-20 mx-auto rounded-2xl items-center border border-white/5 shadow-inner"
                data-aos="flip-up"
                data-aos-delay={`${350 + index * 100}`}
              >
                {feature.icon}
              </div>

              <h3
                className="sm:text-xl text-lg font-bold text-white mb-4 relative z-10 group-hover:text-cyan-300 transition-colors duration-300"
                data-aos="fade-up"
                data-aos-delay={`${400 + index * 100}`}
              >
                {feature.title}
              </h3>

              <p
                className="text-gray-400 text-sm relative z-10 group-hover:text-gray-300 transition-colors duration-300 leading-relaxed flex-grow"
                data-aos="fade-up"
                data-aos-delay={`${450 + index * 100}`}
              >
                {feature.desc}
              </p>

              <div className="absolute inset-0 bg-gradient-to-t from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>
            </li>
          ))}
        </ul>

      </div>
    </section>
  );
};

export default WhyChooseUs;